
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImagePlus } from 'lucide-react';

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [productPlatform, setProductPlatform] = useState('internal');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Would implement product creation logic here
    console.log('Creating new product');
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Add New Product</DialogTitle>
          <DialogDescription>
            Create a new merchandise product to sell in your store.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="details">Basic Details</TabsTrigger>
              <TabsTrigger value="variants">Variants</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="product-name">Product Name</Label>
                  <Input id="product-name" placeholder="e.g. Tour Logo T-Shirt" required />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="product-category">Category</Label>
                  <Select defaultValue="apparel">
                    <SelectTrigger id="product-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apparel">Apparel</SelectItem>
                      <SelectItem value="music">Music</SelectItem>
                      <SelectItem value="collectibles">Collectibles</SelectItem>
                      <SelectItem value="accessories">Accessories</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="product-price">Price</Label>
                  <div className="flex">
                    <Select defaultValue="USD">
                      <SelectTrigger className="w-24 rounded-r-none">
                        <SelectValue placeholder="Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      id="product-price"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="rounded-l-none"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="product-inventory">Inventory</Label>
                  <Input id="product-inventory" type="number" min="0" placeholder="0" required />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="product-description">Description</Label>
                  <Textarea
                    id="product-description"
                    placeholder="Describe your product..."
                    className="min-h-24"
                    required
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="product-platform">Platform</Label>
                  <Select defaultValue="internal" onValueChange={setProductPlatform}>
                    <SelectTrigger id="product-platform">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">Manage in this app</SelectItem>
                      <SelectItem value="shopify">Create in Shopify</SelectItem>
                      <SelectItem value="woocommerce">Create in WooCommerce</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {productPlatform !== 'internal' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      This product will be created in your {productPlatform === 'shopify' ? 'Shopify' : 'WooCommerce'} store and synced with this app.
                    </p>
                  )}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="variants" className="space-y-4">
              <div className="bg-muted p-4 rounded-md">
                <p className="text-sm">
                  Add variants for your product, such as size, color, or material options.
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  Add Variant Option
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                No variants added yet. Add variant options to create product variations.
              </p>
            </TabsContent>
            
            <TabsContent value="images" className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <ImagePlus className="mx-auto h-8 w-8 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium">Drag images here</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  or click to browse files
                </p>
                <Button variant="outline" size="sm" className="mt-4">
                  Upload Images
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended image size: 1000x1000px. Max file size: 5MB.
              </p>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Create Product</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProductModal;
